import Spinner from 'src/components/ui/spinner';
import React, { Dispatch, SetStateAction, useEffect, useState } from 'react';
import './styles.scss';

interface IAccordionProps {
    label: string;
    title: string;
    subtitle: string;
    options: Array<{}>;
    selectedOptions: Array<string>;
    setSelectedOptions: Dispatch<SetStateAction<Array<string>>>;
    disabled?: boolean;
    isOpenDefault?: boolean;
    valueProp: string;
    nameProp: string;
    isLoading: boolean;
    displayCheckboxInRow?: boolean;
}

interface ICheckboxProps {
    name?: string;
    label: string;
    value: string;
    option?: object;
    data: Array<string>;
    setData: Dispatch<SetStateAction<Array<string>>>;
}

interface ICheckboxWrapperProps {
    visibleClass: string;
    valueProp: string;
    nameProp: string;
    options: Array<{}>;
    data: Array<string>;
    setData: Dispatch<SetStateAction<Array<string>>>;
    displayCheckboxInRow: boolean;
}

const Checkbox: React.FC<ICheckboxProps> = ({
    label,
    value,
    data,
    setData,
}) => {
    const [isChecked, setIsChecked] = useState<boolean>(false);
    const onChangeHandler = (event: any) => {
        const isExists = data?.includes(value);
        if (event.currentTarget.checked && !isExists) {
            setData([...data, value]);
            setIsChecked(true);
        } else {
            const x = data.filter((v) => v != value);
            setData(x);
            setIsChecked(false);
        }
    };

    useEffect(() => {
        const isExists = data?.includes(value);
        setIsChecked(isExists);
    }, [data]);

    return (
        <label className='c-checkbox'>
            <input
                className='c-checkbox__input'
                type='checkbox'
                value={value}
                onChange={onChangeHandler}
                checked={isChecked}
            />
            <span className='c-checkbox__label'>{label}</span>
        </label>
    );
};

const CheckboxWrapper: React.FC<ICheckboxWrapperProps> = ({
    visibleClass,
    options,
    setData,
    data,
    valueProp,
    nameProp,
    displayCheckboxInRow,
}) => {
    return (
        <div className={`accordion-picker ${visibleClass}`}>
            <div
                className={`accordion-picker-box ${
                    displayCheckboxInRow ? 'fx-row' : ''
                }`}
            >
                {options?.map((option: any, index) => {
                    return (
                        <Checkbox
                            label={option[nameProp]}
                            name={nameProp}
                            value={option[valueProp]}
                            key={`accordion-${option[nameProp]}-${index}`}
                            option={option}
                            data={data}
                            setData={setData}
                        />
                    );
                })}
            </div>
        </div>
    );
};

const Accordion: React.FC<IAccordionProps> = ({
    label,
    title,
    subtitle,
    options,
    selectedOptions,
    setSelectedOptions,
    valueProp,
    nameProp,
    disabled = false,
    isOpenDefault = false,
    isLoading,
    displayCheckboxInRow = false,
}) => {
    const [isOpen, setIsOpen] = useState<boolean>(isOpenDefault ?? true);
    const expandedClass = isOpen
        ? 'c-icon--[semantic-collapse]'
        : 'c-icon--[semantic-expand]';

    const onClickHeaderHandler = () => {
        if (!disabled) setIsOpen(!isOpen);
    };

    const isVisibleClass = isOpen ? 'visible' : 'hide';
    const disabledClass = disabled ? ' disabled' : '';

    useEffect(() => {
        if (disabled) setIsOpen(false);
    }, [disabled]);

    return (
        <div className='field-user-container__body-accordion-container'>
            <div>
                <h5>
                    {title}
                    {!!selectedOptions.length &&
                        ` (${selectedOptions.length} selected)`}
                </h5>
                <span>{subtitle}</span>
            </div>
            <div
                className={'accordion-header' + disabledClass}
                onClick={onClickHeaderHandler}
            >
                <span className='accordion-select'>
                    <h3 className={'accordion-id' + disabledClass}>{label}</h3>
                </span>
                <div className={'accordion-icon' + disabledClass}>
                    <i
                        className={`c-icon c-icon--small ${expandedClass}`}
                        role='img'
                        aria-hidden='true'
                    ></i>
                </div>
            </div>
            {isLoading ? (
                <div className='loader_container'>
                    <Spinner />
                </div>
            ) : (
                <CheckboxWrapper
                    visibleClass={isVisibleClass}
                    options={options}
                    setData={setSelectedOptions}
                    data={selectedOptions}
                    valueProp={valueProp}
                    nameProp={nameProp}
                    displayCheckboxInRow={displayCheckboxInRow}
                />
            )}
        </div>
    );
};

export default Accordion;
